import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";

interface Material {
  name: string;
  quantity: number;
  estimated_cost: number;
}

interface QuoteEditorProps {
  materials: Material[];
  laborCost: number;
  onUpdate: (materials: Material[], laborCost: number) => void;
}

export const QuoteEditor = ({ materials: initialMaterials, laborCost: initialLabor, onUpdate }: QuoteEditorProps) => {
  const [materials, setMaterials] = useState<Material[]>(initialMaterials);
  const [laborCost, setLaborCost] = useState(initialLabor);

  useEffect(() => {
    onUpdate(materials, laborCost);
  }, [materials, laborCost]);

  const addMaterial = () => {
    setMaterials([...materials, { name: "", quantity: 1, estimated_cost: 0 }]);
  };

  const removeMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };

  const updateMaterial = (index: number, field: keyof Material, value: any) => {
    const updated = [...materials];
    updated[index] = { ...updated[index], [field]: value };
    setMaterials(updated);
  };

  const totalMaterialsCost = materials.reduce((sum, m) => sum + (m.quantity * m.estimated_cost), 0);
  const total = totalMaterialsCost + laborCost;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Quote</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">Materials</Label>
            <Button onClick={addMaterial} size="sm" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
          
          {materials.map((material, index) => (
            <div key={index} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-5">
                <Label htmlFor={`name-${index}`} className="text-xs">Name</Label>
                <Input
                  id={`name-${index}`}
                  value={material.name}
                  onChange={(e) => updateMaterial(index, "name", e.target.value)}
                  placeholder="Material name"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor={`qty-${index}`} className="text-xs">Qty</Label>
                <Input
                  id={`qty-${index}`}
                  type="number"
                  min="1"
                  value={material.quantity}
                  onChange={(e) => updateMaterial(index, "quantity", Number(e.target.value))}
                />
              </div>
              <div className="col-span-3">
                <Label htmlFor={`cost-${index}`} className="text-xs">Cost (RM)</Label>
                <Input
                  id={`cost-${index}`}
                  type="number"
                  min="0"
                  step="0.01"
                  value={material.estimated_cost}
                  onChange={(e) => updateMaterial(index, "estimated_cost", Number(e.target.value))}
                />
              </div>
              <div className="col-span-2 flex items-end">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeMaterial(index)}
                  className="h-10 w-10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-2 pt-4 border-t">
          <div className="flex justify-between items-center">
            <Label htmlFor="labor">Labor Cost (RM)</Label>
            <Input
              id="labor"
              type="number"
              min="0"
              step="0.01"
              value={laborCost}
              onChange={(e) => setLaborCost(Number(e.target.value))}
              className="w-32"
            />
          </div>
          
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span>Materials Total:</span>
            <span>RM {totalMaterialsCost.toFixed(2)}</span>
          </div>
          
          <div className="flex justify-between items-center text-lg font-bold pt-2 border-t">
            <span>Total:</span>
            <span className="text-primary">RM {total.toFixed(2)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
